import {
    type MaybeRefOrGetter,
    computed,
    toValue,
    watch,
} from 'vue';
import type {
    Market,
    MarketId,
    OutcomeParameterValue,
} from '../../types';
import type {
    GlobalFilterOverrides,
    KeyComposedOfMarketAndFilters,
    OutcomeToDisplay,
    FiltersByOutcomeParameterMap,
    OutcomesCtids,
    SetActiveFilterMethod,
    MarketView,
} from './types';
import { buildOutcomesNamesByCtid } from './buildOutcomesNamesByCtid';
import { buildOutcomesWithFilters } from './buildOutcomesWithFilters';
import { filterByMarket } from './filterByMarket';
import { buildNonNumericFiltersGroupKey } from './utils/buildNonNumericFiltersGroupKey';
import { createFiltersByOutcomeParameterMap } from './utils/createFiltersByOutcomeParameterMap';
import { filterDefault } from './filters/filterDefault';
import { createProfitableTotalSelector } from './utils/createProfitableTotalSelector';
import { getNumericFilter } from './utils/getNumericFilter';
import { getKeyComposedOfMarketAndFilters } from './utils/getKeyComposedOfMarketAndFilters';
import { getMiddleNumericFilterValue } from './utils/getMiddleNumericFilterValue';

export function useMarkets(
    _markets: MaybeRefOrGetter<Market[]>,
) {
    const markets = computed(() => toValue(_markets));
    const outcomesNamesByCtid = computed(() => buildOutcomesNamesByCtid(markets.value));
    const builtOutcomesWithFilters = computed(() => buildOutcomesWithFilters(markets.value, outcomesNamesByCtid.value));
    // Позволяет запоминать выбранные вручную значения при любых фильтрах
    const cacheOfActiveFilterValues: Map<KeyComposedOfMarketAndFilters, OutcomeParameterValue> = new Map();

    // helpers

    function getMarketById(id: MarketId): Market | undefined {
        return markets.value.find((market) => market.id === id);
    }

    function getFiltersByMarketId(id: MarketId): FiltersByOutcomeParameterMap | undefined {
        return builtOutcomesWithFilters.value.filtersByMarketId.get(id);
    }

    function getOrCreateFiltersByMarketId(id: MarketId): FiltersByOutcomeParameterMap {
        return getFiltersByMarketId(id) ?? createFiltersByOutcomeParameterMap();
    }

    function getCachedActiveFilterValue(
        id: MarketId,
        filters: FiltersByOutcomeParameterMap,
        globalFilterOverrides: GlobalFilterOverrides,
    ): OutcomeParameterValue | undefined {
        const key = getKeyComposedOfMarketAndFilters(id, filters, globalFilterOverrides);
        return cacheOfActiveFilterValues.get(key);
    }

    function setCachedActiveFilterValue(
        id: MarketId,
        value: OutcomeParameterValue,
        filters: FiltersByOutcomeParameterMap,
        globalFilterOverrides: GlobalFilterOverrides,
    ): void {
        const key = getKeyComposedOfMarketAndFilters(id, filters, globalFilterOverrides);
        cacheOfActiveFilterValues.set(key, value);
    }

    function setActiveFilterValue(id: MarketId, filterName: string, value: string): void {
        const filter = getFiltersByMarketId(id)?.get(filterName);

        if (!filter) return;

        filter.activeValue.value = value;
    }

    function getOutcomesCtidsByMarketId(id: MarketId): OutcomesCtids | undefined {
        return builtOutcomesWithFilters.value.outcomesCtidsByMarketId.get(id);
    }

    function getFilteredOutcomesByMarketId(
        id: MarketId,
        globalFilterOverrides: GlobalFilterOverrides,
    ): OutcomeToDisplay[] {
        const market = getMarketById(id);

        if (!market) return [];

        const outcomes = builtOutcomesWithFilters.value.outcomesByMarketId.get(id) ?? [];
        const outcomesCtids = getOutcomesCtidsByMarketId(id);
        const filtersMap = getOrCreateFiltersByMarketId(id);

        return filterByMarket(
            market,
            outcomes,
            outcomesCtids,
            filtersMap,
            globalFilterOverrides
        );
    }

    /**
     * Устанавливает activeValue по умолчанию, при смене фильтров или рынка.
     * Приоритет:
     * 1 - ранее выбранное вручную значение,
     * 2 - самый выгодный для БК тотал (по задаче),
     * 3 - значение посередине списка
     */
    function setDefaultActiveValueOfNumericFilter(
        id: MarketId,
        globalFilterOverrides: GlobalFilterOverrides,
        availableValues: OutcomeParameterValue[],
    ): void {
        const market = getMarketById(id);
        const filtersMap = getOrCreateFiltersByMarketId(id);

        const numericFilter = getNumericFilter(filtersMap);

        if (!numericFilter || availableValues.length === 0) {
            return;
        }

        const availableValuesSet = new Set(availableValues);
        const savedValue = getCachedActiveFilterValue(id, filtersMap, globalFilterOverrides);

        if (savedValue && availableValuesSet.has(savedValue)) {
            numericFilter.activeValue.value = savedValue;
            return;
        }

        if (market?.kind === 'Total') {
            const outcomes = builtOutcomesWithFilters.value.outcomesByMarketId.get(id) ?? [];
            const profitableTotalSelector = createProfitableTotalSelector();
            const filteredOutcomes = filterDefault(outcomes, filtersMap, globalFilterOverrides);

            for (const { outcome, paramsMap } of filteredOutcomes) {
                const totalForOutcome = paramsMap.get(numericFilter.name)?.values().next().value

                if (!totalForOutcome || !availableValuesSet.has(totalForOutcome)) {
                    continue;
                }

                profitableTotalSelector.addCoefByNumericValue(totalForOutcome, outcome.value);
            }

            const profitableNumericValue = profitableTotalSelector.findProfitableNumericValue();

            if (profitableNumericValue) {
                numericFilter.activeValue.value = profitableNumericValue;
                return;
            }
        }

        numericFilter.activeValue.value = getMiddleNumericFilterValue(availableValues);
    }

    /**
     * Возвращает подмножество значений фильтра NUMERIC_PARAM_VALUE для указанного рынка,
     * которые при текущем выборе остальных фильтров (с учетом globalFilterOverrides)
     * имеют хотя бы один outcome.
     */
    function getAvailableNumericFilterValuesByMarketId(
        id: MarketId,
        globalFilterOverrides: GlobalFilterOverrides,
    ): OutcomeParameterValue[] {
        const filtersMap = getOrCreateFiltersByMarketId(id);
        const numericValuesIndex = builtOutcomesWithFilters.value.numericValuesIndexByMarketId.get(id);
        const numericFilter = getNumericFilter(filtersMap);

        if (!numericFilter) {
            return [];
        }

        // если индекса нет (рынок не Total или Handicap), возвращаем все значения как есть
        if (!numericValuesIndex) {
            return numericFilter.values;
        }

        const groupKey = buildNonNumericFiltersGroupKey(filtersMap, globalFilterOverrides);
        const availableNumericValues = numericValuesIndex.get(groupKey);

        if (!availableNumericValues || availableNumericValues.size === 0) {
            return [];
        }

        return numericFilter.values.filter((v) => availableNumericValues.has(v));
    }

    /**
     * фабрика реактивного представления одного рынка над уже построенным контекстом useMarkets
     * возвращает набор computed-значений, готовых к использованию в шаблоне
     */
    function createMarketView(
        id: MaybeRefOrGetter<MarketId>,
        globalFilterOverrides?: MaybeRefOrGetter<GlobalFilterOverrides | undefined>,
    ): MarketView {
        const overrides = computed(() => toValue(globalFilterOverrides) ?? {});

        const market = computed(() => getMarketById(toValue(id)));

        const filters = computed(() => getOrCreateFiltersByMarketId(toValue(id)));

        const filteredOutcomes = computed(() => {
            return getFilteredOutcomesByMarketId(toValue(id), overrides.value);
        });

        const availableNumericFilterValues = computed(() => {
            return getAvailableNumericFilterValuesByMarketId(toValue(id), overrides.value);
        });

        watch(
            availableNumericFilterValues,
            (availableValues) => {
                setDefaultActiveValueOfNumericFilter(toValue(id), overrides.value, availableValues);
            },
            { immediate: true },
        );

        const setActiveFilter: SetActiveFilterMethod = (filterName, value) => {
            setActiveFilterValue(toValue(id), filterName, value);
            setCachedActiveFilterValue(toValue(id), value, filters.value, overrides.value);
        }

        return {
            market,
            filters,
            filteredOutcomes,
            availableNumericFilterValues,
            setActiveFilter,
        };
    }

    return {
        getFiltersByMarketId: getOrCreateFiltersByMarketId,
        createMarketView,
    };
}
